import { useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withToast } from "../../contexts/toastr.context";
import { CommunityPac } from "../../types/community/community-pac.type";
import { Community } from "../../types/community/community.type";
import { User } from "../../types/user/minimal-user.type";
import { Button } from "../../_components/button.component";
import {
  ConvertToSlugString,
  CreateSlugComponent,
  limitMaxCharacterTo255,
} from "../../_components/create-slug-modal";
import { ErrorDiv } from "../../_components/error.component";
import { UploadFile } from "../../_components/form-controls/upload-file.component";
import PremiumChip from "../../_components/premium/premium-chip.component";
import PremiumFeature from "../../_components/premium/premium-feature.component";
import { TextInput } from "../../_components/text-input.component";
import { MAIN_DOMAIN_SUFFIX } from "../../_config/helper.config";
import Timezones from "../../_config/timezones.config.json";
import { CommunityService } from "../../_service";
import { setCommunity } from "../../_store/_actions/community.actions";

let timezones: string[] = [];
Timezones.map((timezone: { utc: string[] }) => {
  timezone.utc.map((utc: string) => {
    timezones.push(utc);
  });
});

timezones = timezones.sort();

export const COMMUNITY_UPDATE_PAGE_ROUTE = "/settings/updatecommunity";

interface CommunityUpdateModalComponentProps {
  user: User;
  community: Community;
  communityPac: CommunityPac;
  onCommunityUpdate?: (e: any) => void;
  addToast?: (e: any) => void;
  setActive?: (e: any) => void;
}

function CommunityUpdateModalComponent({
  user,
  community,
  communityPac,
  onCommunityUpdate = (e) => {},
  addToast = (e) => {},
  setActive = (e) => {},
}: CommunityUpdateModalComponentProps) {
  const [name, setName] = useState(community?.name || "");
  const [address, setAddress] = useState(community?.address || "");
  const [picture, setPicture] = useState(community?.logo || "");

  const [favicon, setFavicon] = useState<File | null>(null);

  const [privacyPolicyLink, setPrivacyPolicyLink] = useState(
    community.privacyPolicyLink
  );
  const [termsAndConditionsLink, setTermsAndConditionsLink] = useState(
    community.termsAndConditionsLink
  );
  const [timezone, setTimezone] = useState(
    community.timezone ? community.timezone : "Asia/Kolkata"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Slugs related
  const [slug, setSlug] = useState(
    community && community.meta && community.meta.slug
      ? community.meta.slug
      : null
  );
  const [slugTitle, setSlugTitle] = useState(
    community && community.meta && community.meta.title
      ? community.meta.title
      : null
  );
  const [slugDescription, setSlugDescription] = useState<string | null>(
    community && community.meta && community.meta.description
      ? community.meta.description
      : null
  );
  const [OGTitle, setOGTitle] = useState(
    community && community.meta && community.meta.openGraphTitle
      ? community.meta.openGraphTitle
      : null
  );
  const [OGDescription, setOGDescription] = useState(
    community && community.meta && community.meta.openGraphDescription
      ? community.meta.openGraphDescription
      : null
  );
  const [OGImage, setOGImage] = useState(
    community && community.meta && community.OGImage ? community.OGImage : null
  );

  const updateCommunity = async () => {
    // check if form can be submitted
    const isFormSubmitable = (() => {
      const isNameValid = name.length >= 2;
      const isAddressValid = /^[A-Za-z0-9-]{3,63}$/.test(address);

      return isNameValid && isAddressValid;
    })();

    if (!isFormSubmitable) {
      // show error and return
      return;
    }

    setIsLoading(true);
    const meta = {
      slug: ConvertToSlugString(slug === null ? name : slug),
      title: slugTitle === null ? name : slugTitle,
      description:
        slugDescription === null
          ? community.meta && community.meta.description
          : slugDescription,
      openGraphTitle: OGTitle === null ? name : OGTitle,
      openGraphDescription:
        OGDescription === null
          ? community.meta && community.meta.openGraphDescription
          : OGDescription,
      openGraphImage:
        OGImage == null
          ? community.meta && community.meta.openGraphImage
          : OGImage,
    };

    try {
      // call api to update community
      let communityUpdateResponse = await CommunityService.updateCommunity(
        community.id,
        name,
        address,
        community.selectedLayout ? community.selectedLayout : "",
        community.selectedType ? community.selectedType : "",
        user,
        timezone,
        privacyPolicyLink,
        termsAndConditionsLink,
        meta
      );

      // upload image if necessary
      if (picture && typeof picture !== "string") {
        communityUpdateResponse = await CommunityService.updateLogo(
          communityUpdateResponse.community.id,
          user,
          picture
        );
      }

      // upload image if necessary
      if (favicon && typeof favicon !== "string") {
        communityUpdateResponse = await CommunityService.updateFavicon(
          communityUpdateResponse.community.id,
          user,
          favicon
        );
      }

      onSuccessfulUpdate(communityUpdateResponse.community);
    } catch (error: any) {
      // community update failed
      setIsLoading(false);
      if (error && error.response && error.response.data.message) {
        if (error.response.data.errors.name) {
          setErrorMessage(error.response.data.errors.name[0]);
        } else if (error.response.data.errors.address) {
          setErrorMessage(error.response.data.errors.address[0]);
        } else if (error.response.data.errors.logo) {
          setErrorMessage(error.response.data.errors.logo[0]);
        } else if (error.response.data.errors.favicon) {
          setErrorMessage(error.response.data.errors.favicon[0]);
        } else {
          setErrorMessage("Something went wrong!");
        }
      } else {
        setErrorMessage("Something went wrong!");
      }
    }
  };

  // do changes required on successful update
  const onSuccessfulUpdate = (updateCommunity: any) => {
    addToast("Community Info Updated");
    // if address has changed, redirect to the new address
    if (updateCommunity.address !== community.address) {
      // create new address
      const newAddress =
        window.location.protocol +
        "//" +
        updateCommunity.address +
        "." +
        window.location.host.replace(community.address + ".", "");
      window.location.assign(newAddress);
    } else {
      setActive(false);
      setIsLoading(false);
      onCommunityUpdate(updateCommunity);
    }
  };

  return (
    <div className="Card rounded shadow theme-bg-surface">
      {/* headers */}
      <div className="pl-4 pr-2 py-4  border-b theme-border-default flex items-center justify-between">
        <span className="font-bold theme-text-heading-2">
          Update {community.name} Community
        </span>
        <div className="theme-text-subtitle-1">ID: {community.id}</div>
      </div>
      <div className="card p-5 pt-0 theme-text-heading-2 mt-2 ">
        {/* logo */}
        <div className="flex flex-col sm:flex-row space-x-4">
          <PictureUpdate picture={picture} setPicture={setPicture} />
          {/* Form fields */}
          <div>
            {/* name */}
            <div className="font-semibold theme-text-heading-2 mt-4">
              Community Name
            </div>
            <TextInput
              placeholder="Enter here"
              validator={(value) => {
                if (value.length < 2) {
                  return "Name invalid!";
                }
              }}
              defaultValue={name}
              className="py-2 focus:outline-none theme-bg-default"
              onChange={(value) => {
                setName(value);
                // set address
              }}
            />
            {/* address */}
            <div className="font-semibold theme-text-heading-2 mt-4">
              Your Community Address
            </div>
            <div className="mb-4">
              <div className="flex">
                <div className="w-1/2">
                  <TextInput
                    placeholder="URL"
                    validator={(value) => {
                      if (!/^[A-Za-z0-9-]{3,63}$/.test(value)) {
                        return "URL invalid!";
                      }
                    }}
                    defaultValue={address}
                    className="py-2 focus:outline-none theme-bg-default"
                    onChange={(value) => {
                      setAddress(value.toLowerCase());
                    }}
                  />
                </div>
                {community.configuration.isWhiteLabel ? (
                  <></>
                ) : (
                  <div className="w-1/2 p-2 font-semibold mt-3 flex items-start">
                    <span className="pr-4">{MAIN_DOMAIN_SUFFIX}</span>
                  </div>
                )}
              </div>
            </div>
            {/* favicon */}
            <div className="font-semibold theme-text-heading-2 mt-4 flex items-centre space-x-1">
              Favicon{" "}
              <PremiumChip
                className={"ml-2"}
                isAvailable={communityPac.favicon}
              />
            </div>
            <div className="mb-4 flex">
              <div className="w-16">
                <PremiumFeature isAvailable={communityPac.favicon}>
                  <UploadFile
                    previewPictureUrl={community.favicon}
                    file={favicon}
                    setFile={setFavicon}
                    emptyPreviewClass="py-2"
                    placeholderLabel="Upload Favicon"
                    showDeleteButton={false}
                  />
                </PremiumFeature>
              </div>
              <div className="p-4 text-xs theme-text-subtitle-1">
                This will be visible on the browser tab (32px by 32px)
              </div>
            </div>
            {/* timezone */}
            <div className="font-semibold theme-text-heading-2 mt-4">
              Timezone
            </div>
            <div className="mb-4">
              <select
                className="border theme-bg-default theme-border-default rounded flex items-center my-3 py-1 px-2"
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                }}>
                {timezones.map((utc, index) => (
                  <option key={index} value={utc}>
                    {utc}
                  </option>
                ))}
              </select>
            </div>
            {/* privacy policy links */}
            <div className="mb-4">
              <div className="font-semibold theme-text-heading-2 mt-4">
                Privacy Policy Link
              </div>
              <TextInput
                placeholder="Enter here"
                defaultValue={privacyPolicyLink}
                className="py-2 focus:outline-none theme-bg-default"
                onChange={(value) => {
                  setPrivacyPolicyLink(value);
                  // set address
                }}
              />
            </div>
            {/* terms and conditions links */}
            <div className="mb-4">
              <div className="font-semibold theme-text-heading-2 mt-4">
                Terms and Conditions Link
              </div>
              <TextInput
                placeholder="Enter here"
                defaultValue={termsAndConditionsLink}
                className="py-2 focus:outline-none theme-bg-default"
                onChange={(value) => {
                  setTermsAndConditionsLink(value);
                  // set address
                }}
              />
            </div>
            <>
              <div className="py-4 mb-2">
                <CreateSlugComponent
                  community={community}
                  setActive={setActive}
                  hideCreateSlug
                  slug={ConvertToSlugString(slug === null ? name : slug)}
                  setSlug={setSlug}
                  title={limitMaxCharacterTo255(
                    slugTitle == null ? name : slugTitle
                  )}
                  setTitle={setSlugTitle}
                  description={limitMaxCharacterTo255(
                    slugDescription == null
                      ? ((community.meta &&
                          community.meta.description) as string)
                      : slugDescription
                  )}
                  setDescription={setSlugDescription}
                  OGTitle={limitMaxCharacterTo255(
                    OGTitle == null ? name : OGTitle
                  )}
                  setOGTitle={(title) => {
                    setOGTitle(title);
                  }}
                  OGDescription={limitMaxCharacterTo255(
                    OGDescription == null
                      ? ((community.meta &&
                          community.meta.openGraphDescription) as string)
                      : OGDescription
                  )}
                  setOGDescription={setOGDescription}
                  OGImage={
                    OGImage === null
                      ? community.meta && community.meta.openGraphImage
                      : OGImage
                  }
                  setOGImage={setOGImage}
                  OgImageSubtitle={
                    "By default it will take up the community banner uploaded with the community"
                  }
                  clearSlugMeta={() => {}}
                />
              </div>
            </>
          </div>
        </div>
        {/* submit button */}

        <Button
          className=" w-full py-2"
          onClick={updateCommunity}
          isLoading={isLoading}
          label={"Update " + community.name + " Community"}
        />
        <ErrorDiv error={errorMessage} />
      </div>
    </div>
  );
}

interface PictureUpdateProps {
  picture: any;
  setPicture: (file: File) => void;
}
/**
 * Component to update a picture resource
 * @param {*} param0
 * @returns
 */

function PictureUpdate({ picture, setPicture }: PictureUpdateProps) {
  const pictureRef = useRef<HTMLInputElement>(null);

  let imageUrl = "";
  try {
    if (picture && typeof picture === "string" && picture.startsWith("http")) {
      imageUrl = picture;
    } else {
      imageUrl = URL.createObjectURL(picture);
    }
  } catch (error) {
    imageUrl = picture;
  }

  return (
    <div
      className="w-48 h-48 cursor-pointer mx-4 sm:mx-0"
      onClick={(e) => {
        pictureRef.current?.click();
      }}>
      {/* file control */}
      <input
        className="hidden"
        type="file"
        ref={pictureRef}
        onChange={(e: any) => {
          setPicture(e.target.files[0]);
        }}
      />
      {picture ? (
        <div className="preview-control border theme-border-default rounded">
          <img src={imageUrl} alt="" />
        </div>
      ) : (
        <div className="flex place-content-center place-items-center border  h-full">
          <div className="flex set-logo theme-text-primary font-semibold text-sm ">
            Select Logo
          </div>
        </div>
      )}
    </div>
  );
}

const CommunityUpdatePage = withToast(
  connect(
    (s: any) => ({
      user: s.auth,
      community: s.community,
      communityPac: s.communityPac,
    }),
    (d) =>
      bindActionCreators(
        {
          onCommunityUpdate: setCommunity,
        },
        d
      )
  )(CommunityUpdateModalComponent)
);

export default CommunityUpdatePage;
